import { streamLocale } from "../../../i18n/streamingPagesPresentation";
import { stText, useStandaloneLanguage } from "../../../i18n/streamingPagesPresentation";
/**
 * CapabilitySnapshot -- Per-server feature parity card.
 *
 * Shows which capabilities are supported by each configured server type.
 * Data sourced from the intent registry (embySupport/jellyfinSupport/plexSupport fields)
 * and settings (which servers are configured). No new API calls.
 *
 * Rendered as a collapsible card below CockpitHeader.
 */
import { useMemo } from "react";

import { cn } from "../../../utils/cn";
import { Icon } from "../../ui/Icon";
import { SlidePanel } from "../../ui/SlidePanel";
import { intents, domains, getPhaseCountsByDomain } from "./intentRegistry";
import type { DomainId } from "./intentRegistry";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CapabilitySnapshotProps {
  open: boolean;
  embyConfigured: boolean;
  jellyfinConfigured: boolean;
  plexConfigured: boolean;
}

// ---------------------------------------------------------------------------
// Capability groups (derived from intents)
// ---------------------------------------------------------------------------

interface CapabilityRow {
  label: string;
  emby: boolean;
  jellyfin: boolean;
  plex: boolean;
}

const deriveCapabilities = (): CapabilityRow[] => {
  // Notable capabilities where server support differs
  const notable: CapabilityRow[] = [
    { label: stText("healthEndpoint"), emby: true, jellyfin: true, plex: true },
    { label: stText("serverStorageInfo"), emby: false, jellyfin: true, plex: false },
    { label: stText("prometheusMetrics"), emby: false, jellyfin: true, plex: false },
    { label: stText("encodingCodecInfo"), emby: true, jellyfin: false, plex: false },
    { label: stText("fFmpegOptions"), emby: true, jellyfin: false, plex: false },
    { label: stText("releaseNotesAPI"), emby: true, jellyfin: false, plex: false },
    { label: stText("activityLog"), emby: true, jellyfin: true, plex: false },
    { label: stText("userManagement"), emby: true, jellyfin: true, plex: false },
    { label: stText("sessionControl"), emby: true, jellyfin: true, plex: true },
    { label: stText("libraryScanTrigger"), emby: true, jellyfin: true, plex: true },
    { label: stText("scheduledTasks"), emby: true, jellyfin: true, plex: false },
    { label: stText("pluginManagement"), emby: true, jellyfin: true, plex: false },
    { label: stText("pluginEnableDisable"), emby: false, jellyfin: true, plex: false },
    { label: stText("quickConnect"), emby: false, jellyfin: true, plex: false },
    { get label() { return stText("dLNABuiltIn"); }, emby: true, jellyfin: false, plex: false },
    { label: stText("backupCreate"), emby: false, jellyfin: true, plex: false },
    { label: stText("stopActiveTranscode"), emby: false, jellyfin: true, plex: false },
    { label: stText("sessionPlayQueue"), emby: true, jellyfin: false, plex: false },
    { label: stText("physicalPathsAPI"), emby: true, jellyfin: false, plex: false },
  ];
  return notable;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CapabilitySnapshot = ({
  open,
  embyConfigured,
  jellyfinConfigured,
  plexConfigured,
}: CapabilitySnapshotProps) => {
  useStandaloneLanguage();
  const capabilities = useMemo(deriveCapabilities, []);

  const overallByDomain = useMemo(() =>
    domains.map((d) => ({
      ...d,
      counts: getPhaseCountsByDomain(d.id),
    })),
  [streamLocale(), ]);

  const totalIntents = intents.length;
  const totalLive = intents.filter((i) => i.wsdStatus === "LIVE").length;
  const embyTotal = intents.filter((i) => i.embySupport).length;
  const jellyfinTotal = intents.filter((i) => i.jellyfinSupport).length;
  const plexTotal = intents.filter((i) => i.plexSupport).length;

  // Only show a server column when that server is configured
  const showEmby = embyConfigured;
  const showJellyfin = jellyfinConfigured;
  const showPlex = plexConfigured;

  return (
    <SlidePanel open={open}>
      <div className="rounded-lg border border-border/60 bg-card mb-6 overflow-hidden">
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center gap-2 mb-4">
            <Icon icon="lucide:gauge" className="h-4 w-4 text-muted-foreground" aria-hidden />
            <h3 className="text-sm font-medium">{stText("serverCapabilitySnapshot")}</h3>
            <span className="text-xs text-muted-foreground ml-auto">{stText("inline11", { value0: totalLive, value1: totalIntents })}</span>
          </div>

          {/* Domain summary row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            {overallByDomain.map((d) => (
              <DomainPill key={d.id} label={d.label} icon={d.icon} counts={d.counts} />
            ))}
          </div>

          {/* Capability parity table */}
          <div className="border border-border/40 rounded overflow-hidden">
            <div className="overflow-x-auto qb-scroll">
              <table className="w-full min-w-[420px] text-xs">
              <thead>
                <tr className="bg-muted/20 border-b border-border/40">
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{stText("capability")}</th>
                  {showEmby && (
                    <th className="text-center px-3 py-2 font-medium text-foreground">{stText("inline12", { value0: embyTotal })}</th>
                  )}
                  {showJellyfin && (
                    <th className="text-center px-3 py-2 font-medium text-foreground">{stText("inline13", { value0: jellyfinTotal })}</th>
                  )}
                  {showPlex && (
                    <th className="text-center px-3 py-2 font-medium text-foreground">{stText("inline14", { value0: plexTotal })}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {capabilities.map((cap, i) => (
                  <tr key={cap.label} className={cn(i % 2 === 0 && "bg-muted/5", "border-b border-border/20 last:border-0")}>
                    <td className="px-3 py-1.5 text-foreground">{cap.label}</td>
                    {showEmby && (
                      <td className="text-center px-3 py-1.5">
                        <SupportIcon supported={cap.emby} />
                      </td>
                    )}
                    {showJellyfin && (
                      <td className="text-center px-3 py-1.5">
                        <SupportIcon supported={cap.jellyfin} />
                      </td>
                    )}
                    {showPlex && (
                      <td className="text-center px-3 py-1.5">
                        <SupportIcon supported={cap.plex} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </SlidePanel>
  );
};

// ---------------------------------------------------------------------------
// DomainPill
// ---------------------------------------------------------------------------

const DomainPill = ({
  label,
  icon,
  counts,
}: {
  label: string;
  icon: string;
  counts: { total: number; live: number; phaseA: number; phaseB: number; phaseC: number };
}) => { useStandaloneLanguage(); return (
  <div className="flex items-center gap-2 px-2.5 py-1.5 rounded border border-border/40 bg-muted/10">
    <Icon icon={icon} className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden />
    <div className="min-w-0">
      <div className="text-xs font-medium text-foreground truncate">{label}</div>
      <div className="text-[10px] text-muted-foreground">{stText("inline15", { value0: counts.live, value1: counts.total })}</div>
    </div>
  </div>
); };

// ---------------------------------------------------------------------------
// SupportIcon
// ---------------------------------------------------------------------------

const SupportIcon = ({ supported }: { supported: boolean }) => {
  useStandaloneLanguage();
  if (supported) {
    return <Icon icon="lucide:check" className="h-3.5 w-3.5 text-emerald-500 inline-block" aria-label={stText("supported")} />;
  }
  return <span className="text-muted-foreground">--</span>;
};
